import React, { useEffect, useRef, useState } from 'react';

import { Camera } from 'lucide-react';

import ProfileAvatar from './ProfileAvatar';
import {
  ABOUT_MAX_LENGTH,
  GAMBLE_PREFERENCES,
  HANDICAP_INDEX_MAX,
  HANDICAP_INDEX_MIN,
  LANGUAGES,
  PACES_OF_PLAY,
} from '../../constants/GolferProfileConstants';
import {
  useUpdateMyProfile,
  useUploadMyProfilePicture,
} from '../../hooks/useMemberProfileApi';
import { MemberProfileDTO } from '../../services/apiClient';
import { showToast } from '../../utils/showToast';
import Button from '../common/Button';
import Drawer from '../common/Drawer';
import FormField from '../common/FormField';

interface ProfileEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  profile: MemberProfileDTO;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  about: string;
  // YYYY-MM-DD, what the date input reads and writes.
  dateOfBirth: string;
  genderId: string;
  // Typed by the member. A plus handicap is typed with its plus ("+2.4").
  handicapIndex: string;
  gamblePreference: string;
  paceOfPlay: string;
  languages: string[];
  playsAsCouple: '' | 'yes' | 'no';
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const PICTURE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const PICTURE_MAX_BYTES = 10 * 1024 * 1024;

// The DTO types the date as a Date, but the generated client hands back the JSON string as-is.
// Both are handled, and only the date part is kept, so no time zone can move it a day.
const toDateInput = (value: Date | string | undefined): string => {
  if (value == null) {
    return '';
  }
  return value instanceof Date
    ? value.toISOString().slice(0, 10)
    : String(value).slice(0, 10);
};

const toHandicapInput = (value: number | undefined): string => {
  if (value == null) {
    return '';
  }
  return value < 0 ? `+${Math.abs(value).toFixed(1)}` : value.toFixed(1);
};

// "+2.4" is a plus handicap, stored as -2.4. Returns undefined for blank, NaN for nonsense.
const parseHandicap = (value: string): number | undefined => {
  const trimmed = value.trim().replace(',', '.');
  if (trimmed.length === 0) {
    return undefined;
  }
  const isPlus = trimmed.startsWith('+');
  const parsed = Number(isPlus ? trimmed.slice(1) : trimmed);
  return isPlus ? -parsed : parsed;
};

const toFormState = (profile: MemberProfileDTO): FormState => ({
  firstName: profile.firstName ?? '',
  lastName: profile.lastName ?? '',
  email: profile.email ?? '',
  phone: profile.phone ?? '',
  about: profile.about ?? '',
  dateOfBirth: toDateInput(profile.dateOfBirth),
  genderId: profile.genderId ?? '',
  handicapIndex: toHandicapInput(profile.handicapIndex),
  gamblePreference: profile.gamblePreference?.toString() ?? '',
  paceOfPlay: profile.paceOfPlay?.toString() ?? '',
  languages: profile.languages ?? [],
  playsAsCouple:
    profile.playsAsCouple == null ? '' : profile.playsAsCouple ? 'yes' : 'no',
});

const errorMessage = (err: unknown, fallback: string): string => {
  // NSwag throws a declared 400 body as-is (ApiResponse, with its message) and anything else as an
  // ApiException (with its own message).
  const failure = err as { message?: unknown } | null;
  return typeof failure?.message === 'string' &&
    failure.message.trim().length > 0
    ? failure.message
    : fallback;
};

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h3 className="mb-4 mt-2 border-b border-gray-200 pb-2 font-display text-base font-semibold text-ink dark:border-white/10 dark:text-white">
    {children}
  </h3>
);

const ProfileEditDrawer: React.FC<ProfileEditDrawerProps> = ({
  isOpen,
  onClose,
  profile,
}) => {
  const [form, setForm] = useState<FormState>(() => toFormState(profile));
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateProfile = useUpdateMyProfile();
  const uploadPicture = useUploadMyProfilePicture();

  // Start from the saved profile every time the drawer opens, so a cancelled edit does not linger.
  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(profile));
      setErrors({});
      setFormError(null);
    }
    // Only on open: re-running when the profile refetches would wipe what the member is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const set =
    <K extends keyof FormState>(key: K) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ): void => {
      const value = e.target.value as unknown as FormState[K];
      setForm((current) => ({ ...current, [key]: value }));
      setErrors((current) => ({ ...current, [key]: undefined }));
    };

  const handlePicture = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    // Cleared so picking the same file again still fires a change.
    e.target.value = '';
    if (file == null) {
      return;
    }

    if (!PICTURE_TYPES.includes(file.type)) {
      showToast('Choose a JPEG, PNG, WebP or GIF image.', 'error');
      return;
    }
    if (file.size > PICTURE_MAX_BYTES) {
      showToast('That image is too big. Choose one under 10 MB.', 'error');
      return;
    }

    try {
      await uploadPicture.mutateAsync(file);
      showToast('Profile picture saved.', 'success');
    } catch (err: unknown) {
      showToast(errorMessage(err, 'Could not save that picture.'), 'error');
    }
  };

  const validate = (): FormErrors => {
    const next: FormErrors = {};

    if (form.firstName.trim().length === 0) {
      next.firstName = 'First name is required.';
    }
    if (form.lastName.trim().length === 0) {
      next.lastName = 'Last name is required.';
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      next.email = 'Enter a valid email address.';
    }
    if (form.about.trim().length > ABOUT_MAX_LENGTH) {
      next.about = `At most ${ABOUT_MAX_LENGTH} characters.`;
    }

    const handicap = parseHandicap(form.handicapIndex);
    if (
      handicap !== undefined &&
      (Number.isNaN(handicap) ||
        handicap < HANDICAP_INDEX_MIN ||
        handicap > HANDICAP_INDEX_MAX)
    ) {
      next.handicapIndex = 'A golf index is between +10.0 and 54.0.';
    }

    return next;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);

    const found = validate();
    setErrors(found);
    if (Object.values(found).some((message) => message != null)) {
      return;
    }

    try {
      await updateProfile.mutateAsync({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        about: form.about.trim() || undefined,
        // Midnight UTC, so the server keeps the day that was picked.
        dateOfBirth: form.dateOfBirth
          ? new Date(`${form.dateOfBirth}T00:00:00Z`)
          : undefined,
        genderId: form.genderId || undefined,
        handicapIndex: parseHandicap(form.handicapIndex),
        gamblePreference: form.gamblePreference
          ? Number(form.gamblePreference)
          : undefined,
        paceOfPlay: form.paceOfPlay ? Number(form.paceOfPlay) : undefined,
        languages: form.languages,
        playsAsCouple:
          form.playsAsCouple === '' ? undefined : form.playsAsCouple === 'yes',
      });
      showToast('Profile saved.', 'success');
      onClose();
    } catch (err: unknown) {
      if ((err as { status?: number })?.status === 409) {
        setErrors({
          email: 'Another account already uses that email address.',
        });
        return;
      }
      setFormError(errorMessage(err, 'Could not save your profile.'));
    }
  };

  const isSaving = updateProfile.isPending;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Edit my profile">
      <form onSubmit={(e) => void handleSubmit(e)} noValidate>
        <SectionHeading>Personal information</SectionHeading>

        <div className="mb-6 flex items-center gap-4">
          <ProfileAvatar
            pictureUrl={profile.profilePictureUrl}
            firstName={profile.firstName}
            lastName={profile.lastName}
            className="h-20 w-20"
          />
          <div>
            <Button
              variant="secondary"
              icon={Camera}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadPicture.isPending}
              isLoading={uploadPicture.isPending}
            >
              Change picture
            </Button>
            <p className="mt-1 text-xs text-ink-muted dark:text-white/50">
              JPEG, PNG, WebP or GIF, up to 10 MB.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept={PICTURE_TYPES.join(',')}
              className="hidden"
              onChange={(e) => void handlePicture(e)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <FormField
            id="firstName"
            label="First name"
            value={form.firstName}
            onChange={set('firstName')}
            error={errors.firstName}
            maxLength={255}
            required
          />
          <FormField
            id="lastName"
            label="Last name"
            value={form.lastName}
            onChange={set('lastName')}
            error={errors.lastName}
            maxLength={255}
            required
          />
        </div>

        <FormField
          id="about"
          label="About yourself"
          type="textarea"
          value={form.about}
          onChange={set('about')}
          error={errors.about}
          maxLength={ABOUT_MAX_LENGTH}
          rows={3}
          placeholder="A line or two other golfers will see."
          helper={`${form.about.length} / ${ABOUT_MAX_LENGTH}`}
        />

        <FormField
          id="email"
          label="Email address"
          type="email"
          value={form.email}
          onChange={set('email')}
          error={errors.email}
          maxLength={255}
          helper="You sign in with this address."
          required
        />

        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <FormField
            id="dateOfBirth"
            label="Date of birth"
            type="date"
            value={form.dateOfBirth}
            onChange={set('dateOfBirth')}
            allowFutureDates={false}
          />
          <FormField
            id="phone"
            label="Mobile"
            type="text"
            value={form.phone}
            onChange={set('phone')}
            maxLength={50}
            placeholder="+27 82 123 4567"
          />
        </div>

        <FormField
          id="genderId"
          label="Gender"
          type="dropdown"
          value={form.genderId}
          onChange={set('genderId')}
          searchable={false}
          placeholder="Not set"
          options={[
            { value: '', label: 'Not set' },
            ...(profile.genderOptions ?? []).map((option) => ({
              value: option.id,
              label: option.name ?? '',
            })),
          ]}
        />

        <SectionHeading>Golfer profile</SectionHeading>

        <FormField
          id="handicapIndex"
          label="Golf index"
          type="text"
          value={form.handicapIndex}
          onChange={set('handicapIndex')}
          error={errors.handicapIndex}
          maxLength={5}
          placeholder="For example 12.4, or +1.2"
        />

        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <FormField
            id="gamblePreference"
            label="Like to gamble"
            type="dropdown"
            value={form.gamblePreference}
            onChange={set('gamblePreference')}
            searchable={false}
            placeholder="Not set"
            options={[
              { value: '', label: 'Not set' },
              ...GAMBLE_PREFERENCES.map((choice) => ({
                value: choice.value.toString(),
                label: choice.label,
              })),
            ]}
          />
          <FormField
            id="paceOfPlay"
            label="Pace of play"
            type="dropdown"
            value={form.paceOfPlay}
            onChange={set('paceOfPlay')}
            searchable={false}
            placeholder="Not set"
            options={[
              { value: '', label: 'Not set' },
              ...PACES_OF_PLAY.map((choice) => ({
                value: choice.value.toString(),
                label: choice.label,
              })),
            ]}
          />
        </div>

        <FormField
          id="languages"
          label="Languages"
          type="multiselect"
          value={form.languages}
          onChange={set('languages')}
          placeholder="Choose the languages you speak"
          options={LANGUAGES.map((language) => ({
            value: language.code,
            label: language.label,
            searchText: language.label,
          }))}
        />

        <FormField
          id="playsAsCouple"
          label="Play as a couple"
          type="dropdown"
          value={form.playsAsCouple}
          onChange={set('playsAsCouple')}
          searchable={false}
          placeholder="Not set"
          options={[
            { value: '', label: 'Not set' },
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
        />

        {formError && (
          <p
            role="alert"
            className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {formError}
          </p>
        )}

        <div className="sticky bottom-0 -mx-6 flex gap-3 border-t border-gray-200 bg-white px-6 py-4 dark:border-white/10 dark:bg-gray-800">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSaving}
            isLoading={isSaving}
            className="flex-1"
          >
            Save
          </Button>
        </div>
      </form>
    </Drawer>
  );
};

export default ProfileEditDrawer;
